import { useState, lazy, Suspense, useEffect } from "react";
import "@/App.css";
import "@/styles/brand.css";
import "@/styles/pages.css";
import "@/styles/forms.css";
import "@/styles/redesign.css";
import "@/styles/responsive.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import LoaderScreen from "@/components/Loader/LoaderScreen";
import Navbar from "@/components/Navbar/Navbar";
import Footer from "@/components/Footer/Footer";
import CustomCursor from "@/components/Cursor/CustomCursor";
import AnnouncementBar from "@/components/AnnouncementBar/AnnouncementBar";
import WhatsAppFloat from "@/components/Floaters/WhatsAppFloat";
import CookieBanner from "@/components/Floaters/CookieBanner";
import Home from "@/pages/Home";
import About from "@/pages/About";
import Services from "@/pages/Services";
import ServiceDetail from "@/pages/ServiceDetail";
import Gallery from "@/pages/Gallery";
import ProjectDetail from "@/pages/ProjectDetail";
import CaseStudies from "@/pages/CaseStudies";
import CaseStudyDetail from "@/pages/CaseStudyDetail";
import Hiring from "@/pages/Hiring";
import Freelance from "@/pages/Freelance";
import Contact from "@/pages/Contact";
import Quote from "@/pages/Quote";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import NotFound from "@/pages/NotFound";
import { AuthProvider } from "@/lib/auth";
import { SettingsProvider } from "@/lib/SettingsContext";

// Lazy-load admin chunk
const AdminLayout = lazy(() => import("@/admin/AdminLayout"));
const AdminLogin = lazy(() => import("@/admin/Login"));
const AdminReset = lazy(() => import("@/admin/ResetPassword"));
const AdminSetup = lazy(() => import("@/admin/Setup"));
const AdminDashboard = lazy(() => import("@/admin/pages/Dashboard"));
const AdminGallery = lazy(() => import("@/admin/pages/Gallery"));
const AdminCaseStudies = lazy(() => import("@/admin/pages/CaseStudies"));
const AdminServices = lazy(() => import("@/admin/pages/Services"));
const AdminCareers = lazy(() => import("@/admin/pages/Careers"));
const AdminSubs = () => import("@/admin/pages/Submissions");
const AdminQuotes = lazy(() => AdminSubs().then(m => ({ default: m.Quotes })));
const AdminMessages = lazy(() => AdminSubs().then(m => ({ default: m.Messages })));
const AdminFreelancers = lazy(() => AdminSubs().then(m => ({ default: m.Freelancers })));
const AdminSet = () => import("@/admin/pages/Settings");
const AdminAppearance = lazy(() => AdminSet().then(m => ({ default: m.Appearance })));
const AdminSEO = lazy(() => AdminSet().then(m => ({ default: m.SEO })));
const AdminSiteSettings = lazy(() => AdminSet().then(m => ({ default: m.SiteSettings })));
const AdminAccount = lazy(() => import("@/admin/pages/Account"));

const PublicShell = ({ children, loaderDone, onLoaderDone }) => (
  <>
    <LoaderScreen onComplete={onLoaderDone} />
    <CustomCursor />
    <div style={{ visibility: loaderDone ? "visible" : "hidden" }}>
      <AnnouncementBar />
      <Navbar />
      {children}
      <Footer />
      <WhatsAppFloat />
      <CookieBanner />
    </div>
  </>
);

const Shell = () => {
  const [loaderDone, setLoaderDone] = useState(false);
  const location = useLocation();
  const isAdmin = location.pathname.startsWith("/admin");

  useEffect(() => {
    if (isAdmin) {
      document.body.classList.add("admin-page");
    } else {
      document.body.classList.remove("admin-page");
    }
    return () => document.body.classList.remove("admin-page");
  }, [isAdmin]);

  // Reset scroll on every route change.
  useEffect(() => {
    requestAnimationFrame(() => window.scrollTo({ top: 0, left: 0, behavior: "auto" }));
  }, [location.pathname]);

  if (isAdmin) {
    return (
      <Suspense fallback={<div style={{ padding: 60, color: "#fff", background: "#06090E", minHeight: "100vh" }}>Loading admin…</div>}>
        <Routes>
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin/reset-password" element={<AdminReset />} />
          <Route path="/admin/setup" element={<AdminSetup />} />
          <Route path="/admin" element={<AdminLayout />}>
            <Route index element={<AdminDashboard />} />
            <Route path="dashboard" element={<AdminDashboard />} />
            <Route path="gallery" element={<AdminGallery />} />
            <Route path="case-studies" element={<AdminCaseStudies />} />
            <Route path="services" element={<AdminServices />} />
            <Route path="careers" element={<AdminCareers />} />
            <Route path="freelancers" element={<AdminFreelancers />} />
            <Route path="quotes" element={<AdminQuotes />} />
            <Route path="messages" element={<AdminMessages />} />
            <Route path="appearance" element={<AdminAppearance />} />
            <Route path="seo" element={<AdminSEO />} />
            <Route path="settings" element={<AdminSiteSettings />} />
            <Route path="account" element={<AdminAccount />} />
          </Route>
        </Routes>
      </Suspense>
    );
  }

  return (
    <PublicShell loaderDone={loaderDone} onLoaderDone={() => setLoaderDone(true)}>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/about" element={<About />} />
        <Route path="/services" element={<Services />} />
        <Route path="/services/:slug" element={<ServiceDetail />} />
        <Route path="/gallery" element={<Gallery />} />
        <Route path="/gallery/:slug" element={<ProjectDetail />} />
        <Route path="/case-studies" element={<CaseStudies />} />
        <Route path="/case-studies/:slug" element={<CaseStudyDetail />} />
        <Route path="/careers/hiring" element={<Hiring />} />
        <Route path="/careers/freelance" element={<Freelance />} />
        <Route path="/careers/freelancers" element={<Freelance />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/quote" element={<Quote />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </PublicShell>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <SettingsProvider>
          <AuthProvider>
            <Shell />
          </AuthProvider>
        </SettingsProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
